import { useState, useRef, useEffect } from 'react'
import { ArrowRight, Mail, ShieldCheck, AlertCircle, Loader } from 'lucide-react'

const ALLOWED_DOMAINS = ['@kgrp.in', '@finkraft.ai']
const API_URL = process.env.REACT_APP_API_URL ?? 'https://vat-recovery.finkraftai.com'

type Step = 'email' | 'checking' | 'otp' | 'denied' | 'success'

function getDomain(email: string) { return '@' + (email.split('@')[1] ?? '').toLowerCase() }
function isAllowed(email: string) { return ALLOWED_DOMAINS.some(d => getDomain(email) === d) }

async function sendOtp(email: string) {
  const res = await fetch(`${API_URL}/api/auth/send-otp`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e?.detail?.message || 'Failed to send code') }
}

async function verifyOtp(email: string, otp: string): Promise<boolean> {
  const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp }),
  })
  if (res.status === 401) return false
  if (!res.ok) throw new Error('Verification failed')
  const data = await res.json()
  if (data.token) sessionStorage.setItem('finkraft_token', data.token)
  return true
}

// Inline SVG logo — crisp on any background, no transparency issues
function FinkraftLogo({ size = 32 }: { size?: number }) {
  // Real logo: teal "fin" + grey "kraft.ai" — transparent bg works on dark panel
  return <img src="/logo.png" alt="finkraft.ai" style={{ height: size, display: "block", filter: "brightness(1.15)" }} />
}

function FinkraftLogoDark({ size = 28 }: { size?: number }) {
  // On light bg: darken the grey portion so "kraft.ai" reads clearly
  return <img src="/logo.png" alt="finkraft.ai" style={{ height: size, display: "block", filter: "brightness(0.55) saturate(1.2)" }} />
}

export default function Login({ onSuccess }: { onSuccess: (email: string) => void }) {
  const [step,    setStep]    = useState<Step>('email')
  const [email,   setEmail]   = useState('')
  const [otp,     setOtp]     = useState(['','','','','',''])
  const [error,   setError]   = useState('')
  const [sending, setSending] = useState(false)
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => { if (step === 'otp') setTimeout(() => otpRefs.current[0]?.focus(), 120) }, [step])

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('')
    const t = email.trim().toLowerCase()
    if (!t.includes('@') || !t.includes('.')) { setError('Enter a valid email address.'); return }
    if (!isAllowed(t)) { setStep('denied'); return }
    setStep('checking')
    try { await sendOtp(t); setStep('otp') }
    catch (err: any) { setStep('email'); setError(err.message || 'Could not send code. Try again.') }
  }

  const handleOtpChange = (i: number, val: string) => {
    const d = val.replace(/\D/,'').slice(-1)
    const n = [...otp]; n[i] = d; setOtp(n)
    if (d && i < 5) otpRefs.current[i+1]?.focus()
    if (n.every(x => x) && d) handleOtpVerify(n.join(''))
  }

  const handleOtpKeyDown = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i-1]?.focus()
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const t = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6)
    if (t.length === 6) { const d = t.split(''); setOtp(d); handleOtpVerify(t); otpRefs.current[5]?.focus() }
    e.preventDefault()
  }

  const handleOtpVerify = async (code: string) => {
    setError(''); setSending(true)
    try {
      const ok = await verifyOtp(email.trim().toLowerCase(), code)
      if (ok) { setStep('success'); setTimeout(() => onSuccess(email.trim().toLowerCase()), 800) }
      else { setError('Incorrect code — please try again.'); setOtp(['','','','','','']); otpRefs.current[0]?.focus() }
    } catch { setError('Verification failed. Try again.') }
    finally { setSending(false) }
  }

  const handleResend = async () => {
    setError(''); setOtp(['','','','','','']); setSending(true)
    try { await sendOtp(email.trim().toLowerCase()) } catch (err: any) { setError(err.message) }
    finally { setSending(false) }
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex', fontFamily:'Inter, system-ui, sans-serif' }}>

      {/* ── LEFT — brand panel ─────────────────────────────────────────────── */}
      <div style={{
        width:'48%', minHeight:'100vh', flexShrink:0,
        background:'#0a1628',
        display:'flex', flexDirection:'column',
        padding:'40px 52px',
        position:'relative', overflow:'hidden',
      }}>
        {/* Top accent */}
        <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#0096c7 0%,#00d4ff 60%,transparent 100%)' }}/>

        {/* Subtle grid texture */}
        <div style={{
          position:'absolute', inset:0, opacity:0.035, pointerEvents:'none',
          backgroundImage:'linear-gradient(#00b4d8 1px, transparent 1px), linear-gradient(90deg, #00b4d8 1px, transparent 1px)',
          backgroundSize:'48px 48px',
        }}/>

        {/* Glow blobs */}
        <div style={{ position:'absolute', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle, rgba(0,150,199,.12) 0%, transparent 70%)', top:'30%', left:'50%', transform:'translate(-50%,-50%)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', width:300, height:300, borderRadius:'50%', background:'radial-gradient(circle, rgba(0,180,216,.08) 0%, transparent 70%)', top:'75%', left:'30%', transform:'translate(-50%,-50%)', pointerEvents:'none' }}/>

        {/* Logo — inline SVG, no background needed */}
        <div style={{ position:'relative', zIndex:1, marginBottom:0 }}>
          <FinkraftLogo size={30} />
        </div>

        {/* Middle content */}
        <div style={{ position:'relative', zIndex:1, marginTop:'auto', marginBottom:'auto', paddingTop:20 }}>
          {/* Tag */}
          <div style={{ display:'inline-flex', alignItems:'center', gap:7, marginBottom:28, padding:'6px 14px', borderRadius:99, background:'rgba(0,150,199,.15)', border:'1px solid rgba(0,150,199,.3)' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:'#0096c7' }}/>
            <span style={{ fontSize:11.5, fontWeight:600, color:'#0096c7', letterSpacing:'1px', textTransform:'uppercase' }}>VAT Recovery Platform</span>
          </div>

          <h1 style={{ fontSize:40, fontWeight:800, color:'#ffffff', lineHeight:1.12, margin:'0 0 22px', letterSpacing:'-0.5px' }}>
            Recover VAT<br/>
            <span style={{ color:'#00b4d8' }}>intelligently.</span>
          </h1>

          <p style={{ fontSize:15.5, color:'rgba(255,255,255,.5)', lineHeight:1.75, maxWidth:360, margin:'0 0 44px' }}>
            Automated analysis against VAT Chart 2025 — built for Indian enterprise finance teams.
          </p>

          {/* Feature list */}
          {[
            '13+ countries with full claim lifecycle',
            'Deadline tracking & internal alerts',
            'Vendor, entity & monthly drill-down',
          ].map(f => (
            <div key={f} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
              <div style={{ width:20, height:20, borderRadius:6, background:'rgba(0,150,199,.2)', border:'1px solid rgba(0,150,199,.4)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#00b4d8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <span style={{ fontSize:13.5, color:'rgba(255,255,255,.6)', lineHeight:1.4 }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Stats + footer */}
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', gap:0, marginBottom:40, padding:'20px 24px', background:'rgba(255,255,255,.04)', borderRadius:14, border:'1px solid rgba(255,255,255,.07)' }}>
            {[['13+','Countries'],['₹0','Setup cost'],['2025','VAT Chart']].map((s,i) => (
              <div key={s[1]} style={{ flex:1, textAlign:'center', borderRight: i < 2 ? '1px solid rgba(255,255,255,.08)' : 'none', padding:'0 8px' }}>
                <p style={{ fontSize:24, fontWeight:800, color:'#00b4d8', margin:'0 0 4px', fontVariantNumeric:'tabular-nums' }}>{s[0]}</p>
                <p style={{ fontSize:11.5, color:'rgba(255,255,255,.35)', margin:0, letterSpacing:'.3px' }}>{s[1]}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize:11.5, color:'rgba(255,255,255,.18)', margin:0 }}>
            © {new Date().getFullYear()} finkraft.ai · K Group · All rights reserved
          </p>
        </div>
      </div>

      {/* ── RIGHT — form panel ─────────────────────────────────────────────── */}
      <div style={{ flex:1, background:'#f8fafc', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'40px 32px', position:'relative' }}>
        {/* Top-right logo for mobile / small screens */}
        <div style={{ position:'absolute', top:28, right:32 }}>
          <FinkraftLogoDark size={22} />
        </div>

        <div style={{ width:'100%', maxWidth:400 }}>

          {/* ── Email ── */}
          {(step === 'email' || step === 'checking') && (
            <>
              <h2 style={{ fontSize:28, fontWeight:800, color:'#0f172a', margin:'0 0 8px', letterSpacing:'-0.3px' }}>
                Sign in
              </h2>
              <p style={{ fontSize:14.5, color:'#64748b', margin:'0 0 32px', lineHeight:1.6 }}>
                Enter your work email to receive a verification code.
              </p>

              <form onSubmit={handleEmailSubmit}>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#374151', marginBottom:8, letterSpacing:'.4px', textTransform:'uppercase' }}>
                  Work email
                </label>
                <div style={{ position:'relative', marginBottom: error ? 6 : 20 }}>
                  <Mail size={15} style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'#94a3b8', pointerEvents:'none' }}/>
                  <input
                    type="email" value={email} autoFocus autoComplete="email"
                    disabled={step === 'checking'}
                    placeholder="you@finkraft.ai"
                    onChange={e => { setEmail(e.target.value); setError('') }}
                    style={{
                      width:'100%', paddingLeft:40, paddingRight:14,
                      paddingTop:14, paddingBottom:14,
                      background:'#fff', border:`1.5px solid ${error?'#fca5a5':'#e2e8f0'}`,
                      borderRadius:12, fontSize:15, color:'#0f172a',
                      outline:'none', fontFamily:'inherit', boxSizing:'border-box',
                      transition:'border-color .15s, box-shadow .15s',
                      boxShadow:'0 1px 3px rgba(0,0,0,.05)',
                    }}
                    onFocus={e => { e.target.style.borderColor='#0096c7'; e.target.style.boxShadow='0 0 0 3px rgba(0,150,199,.1)' }}
                    onBlur={e => { e.target.style.borderColor=error?'#fca5a5':'#e2e8f0'; e.target.style.boxShadow='0 1px 3px rgba(0,0,0,.05)' }}
                  />
                </div>

                {error && (
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:16, color:'#b91c1c', fontSize:12.5 }}>
                    <AlertCircle size={13}/>{error}
                  </div>
                )}

                <button type="submit" disabled={step==='checking' || !email.trim()} style={{
                  width:'100%', padding:'15px 0', borderRadius:12,
                  background: step==='checking' ? '#0284c7' : '#0096c7',
                  color:'#fff', border:'none', fontFamily:'inherit',
                  fontSize:15, fontWeight:700,
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                  cursor: step==='checking' ? 'not-allowed' : 'pointer',
                  boxShadow:'0 4px 16px rgba(0,150,199,.35)',
                  transition:'background .15s, transform .1s, box-shadow .15s',
                }}
                onMouseEnter={e => { if (step!=='checking'){ (e.currentTarget as any).style.background='#007aa3'; (e.currentTarget as any).style.boxShadow='0 6px 20px rgba(0,150,199,.45)' }}}
                onMouseLeave={e => { (e.currentTarget as any).style.background=step==='checking'?'#0284c7':'#0096c7'; (e.currentTarget as any).style.boxShadow='0 4px 16px rgba(0,150,199,.35)' }}
                >
                  {step==='checking'
                    ? <><Loader size={15} style={{ animation:'spin .7s linear infinite' }}/>Sending code…</>
                    : <>Continue <ArrowRight size={15}/></>
                  }
                </button>
              </form>

              <div style={{ marginTop:20, display:'flex', alignItems:'center', gap:8, padding:'11px 14px', borderRadius:10, background:'rgba(0,150,199,.06)', border:'1px solid rgba(0,150,199,.15)' }}>
                <ShieldCheck size={14} color="#0096c7" style={{ flexShrink:0 }}/>
                <p style={{ fontSize:12.5, color:'#0369a1', margin:0 }}>
                  Restricted to <strong>@finkraft.ai</strong> and <strong>@kgrp.in</strong> only
                </p>
              </div>
            </>
          )}

          {/* ── OTP ── */}
          {(step === 'otp' || step === 'success') && (
            <>
              <div style={{ marginBottom:28, textAlign:'center' }}>
                <div style={{
                  width:56, height:56, borderRadius:16, margin:'0 auto 18px',
                  background: step==='success' ? '#f0fdf4' : '#eff6ff',
                  border:`2px solid ${step==='success'?'#86efac':'#93c5fd'}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  transition:'all .4s',
                }}>
                  {step==='success' ? <ShieldCheck size={26} color="#16a34a"/> : <Mail size={24} color="#3b82f6"/>}
                </div>
                <h2 style={{ fontSize:26, fontWeight:800, color:'#0f172a', margin:'0 0 8px' }}>
                  {step==='success' ? 'Verified!' : 'Check your inbox'}
                </h2>
                <p style={{ fontSize:14, color:'#64748b', margin:0, lineHeight:1.6 }}>
                  {step==='success'
                    ? 'Taking you to your dashboard…'
                    : <>We sent a 6-digit code to<br/><strong style={{ color:'#0f172a' }}>{email}</strong></>
                  }
                </p>
              </div>

              {step==='otp' && (<>
                <div style={{ display:'flex', gap:8, justifyContent:'center', marginBottom:6 }}>
                  {otp.map((d,i) => (
                    <input key={i} ref={el => { otpRefs.current[i]=el }}
                      type="text" inputMode="numeric" maxLength={1} value={d}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      onPaste={i===0 ? handleOtpPaste : undefined}
                      disabled={sending}
                      style={{
                        width:54, height:62, textAlign:'center',
                        fontSize:26, fontWeight:800, color:'#0f172a',
                        border:`2px solid ${d?'#0096c7':error?'#fca5a5':'#e2e8f0'}`,
                        borderRadius:12, outline:'none', fontFamily:'Inter,monospace',
                        background: d ? '#f0f9ff' : '#fff',
                        boxShadow: d ? '0 0 0 3px rgba(0,150,199,.1)' : '0 1px 3px rgba(0,0,0,.05)',
                        transition:'all .15s', caretColor:'transparent',
                      }}
                    />
                  ))}
                </div>

                {error && <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginBottom:10, color:'#b91c1c', fontSize:12.5 }}><AlertCircle size={13}/>{error}</div>}
                {sending && <div style={{ textAlign:'center', color:'#0096c7', fontSize:13, marginBottom:10, display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}><Loader size={13} style={{ animation:'spin .7s linear infinite' }}/>Verifying…</div>}

                <div style={{ textAlign:'center', marginTop:22 }}>
                  <p style={{ fontSize:13, color:'#94a3b8', marginBottom:6 }}>Didn't receive it?</p>
                  <button onClick={handleResend} disabled={sending} style={{ background:'none', border:'none', cursor:'pointer', color:'#0096c7', fontSize:13.5, fontWeight:600, fontFamily:'inherit', padding:0 }}>
                    Resend code
                  </button>
                </div>
                <button onClick={() => { setStep('email'); setOtp(['','','','','','']); setError('') }}
                  style={{ display:'flex', alignItems:'center', gap:5, margin:'18px auto 0', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:12.5, fontFamily:'inherit' }}>
                  ← Different email
                </button>
              </>)}
            </>
          )}

          {/* ── Denied ── */}
          {step==='denied' && (
            <>
              <div style={{ marginBottom:24 }}>
                <div style={{ width:52, height:52, borderRadius:14, marginBottom:18, background:'#fef2f2', border:'2px solid #fecaca', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <AlertCircle size={24} color="#b91c1c"/>
                </div>
                <h2 style={{ fontSize:26, fontWeight:800, color:'#0f172a', margin:'0 0 8px' }}>Access denied</h2>
                <p style={{ fontSize:14, color:'#64748b', margin:0, lineHeight:1.6 }}>
                  <code style={{ background:'#fef2f2', color:'#b91c1c', padding:'1px 7px', borderRadius:5, fontSize:13 }}>{getDomain(email)}</code>
                  {' '}is not an authorised domain.
                </p>
              </div>
              <div style={{ background:'#f8fafc', borderRadius:12, padding:'16px 18px', border:'1px solid #e2e8f0', marginBottom:22 }}>
                <p style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.7px', marginBottom:10 }}>Authorised domains</p>
                {ALLOWED_DOMAINS.map(d => (
                  <div key={d} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:'#0096c7', flexShrink:0 }}/>
                    <code style={{ fontSize:13.5, color:'#0f172a', fontFamily:'JetBrains Mono, monospace' }}>{d}</code>
                  </div>
                ))}
                <p style={{ fontSize:12, color:'#94a3b8', margin:'10px 0 0', lineHeight:1.5 }}>Contact your administrator if you believe this is a mistake.</p>
              </div>
              <button onClick={() => { setStep('email'); setEmail(''); setError('') }} style={{ width:'100%', padding:'13px 0', borderRadius:12, background:'#0f1b35', color:'#fff', border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:14.5, fontWeight:700 }}>
                Try a different email
              </button>
            </>
          )}

          <p style={{ textAlign:'center', fontSize:11.5, color:'#cbd5e1', marginTop:32 }}>
            Secured by finkraft.ai · All data encrypted in transit
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 800px) { .login-left { display: none !important; } }
      `}</style>
    </div>
  )
}